import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import i18n from '../../i18n';
import { starteGoogleAnmeldung } from '../../utils/googleAnmeldung';
import {
  UEBERGABE_PARAM,
  NACHRICHT_BEREIT,
  UEBERGABE_FRIST_MS,
  istGueltigeTokenNachricht,
  leseSupportClaims,
  uebergabeLaeuft,
  darfRefreshSchreiben,
} from '../utils/supportUebergabe';

// Wendet die Account-Sprache (user.preferredLanguage) auf die UI an — aber nur, wenn der Nutzer
// auf diesem Gerät noch KEINE bewusste Sprachwahl getroffen hat (kein 'bispicy-language' im
// localStorage). So greift die im Profil gespeicherte Sprache auf einem frisch eingeloggten
// Gerät (z. B. dem Handy), ohne einen lokalen Override (Footer-Umschalter) bei jedem Reload zu
// zerstören. changeLanguage lädt das EN/TH-Paket bei Bedarf nach und cacht die Wahl in localStorage.
function applyAccountLanguage(user?: { preferredLanguage?: string } | null): void {
  try {
    const pref = user?.preferredLanguage;
    if (!pref) return;
    if (localStorage.getItem('bispicy-language')) return;
    if ((i18n.language || '').split('-')[0] === pref) return;
    i18n.changeLanguage(pref);
  } catch {
    // i18n/localStorage nicht verfügbar — UI bleibt beim Detector-Default
  }
}

export interface UnifiedUser {
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  phone: string | null;
  nickname?: string | null;
  billingStreet?: string | null;
  billingCity?: string | null;
  billingPostalCode?: string | null;
  billingCountry?: string | null;
  vatId?: string | null;
  timezone?: string | null;
  isEmailVerified: boolean;
  preferredLanguage: string;
  stripeCustomerId: string | null;
  tenantId: string | null;
  googleId: string | null;
  hasUserPassword: boolean;
  createdAt: string;
  // Welle 1 Paket 1 (V-0086): war im Backend (UnifiedAuthService::formatUser()) längst da,
  // hier aber nie deklariert — Geldknöpfe (Tarifwechsel/Zahlweise) brauchen sie, um zwischen
  // dem Konto-Inhaber (roles enthält 'admin') und anderen Kundencenter-Konten zu unterscheiden.
  roles?: string[];
}

/**
 * Korrekturrunde V-0086 (R9, Ben): DIESELBE Prüfung wie serverseitig
 * (`MasterUserProvider::loadUserByIdentifier()`, `$primaryRole = $roles[0] ?? 'admin';`) —
 * NICHT `roles.includes('admin')`. Ein Konto mit `roles: ['viewer', 'admin']` wäre mit
 * `includes()` fälschlich ein Admin, obwohl der Server nur die ERSTE Rolle als effektive Rolle
 * behandelt. Ohne Rollen-Array (Altkonten) gilt wie am Server: Rückfall auf `'admin'`.
 */
export function istKontoAdmin(user: Pick<UnifiedUser, 'roles'> | null | undefined): boolean {
  return (user?.roles?.[0] ?? 'admin') === 'admin';
}

interface License {
  id: string;
  productType: string;
  productId: string;
  productCode: string;
  productName: string;
  status: string;
  features: string[];
  maxDevices: number;
  startsAt: string;
  expiresAt: string;
  stripeSubscriptionId: string | null;
}

interface AuthState {
  user: UnifiedUser | null;
  licenses: License[];
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface MyPortalAuthContextType extends AuthState {
  // 🚨 `tenantId` und `tenants` gehören zum Multi-Tenant-Login (eine E-Mail bei mehreren
  // Mandanten): Bei mehreren Treffern antwortet der Server mit `code='tenant_selection_required'`
  // und der Mandantenliste, die Maske wiederholt den Aufruf mit der gewählten `tenantId`.
  // Die Implementierung kann das längst — nur diese Signatur kannte beides nicht.
  login: (email: string, password: string, tenantId?: string) => Promise<{
    success: boolean;
    error?: string;
    code?: string;
    // V-2521: Wartezeit der Anmelde-Drosselung (Code `anmeldung_gedrosselt`, HTTP 429).
    retry_after_seconds?: number;
    tenants?: Array<{ tenantId: string; companyName: string; userId: string }>;
  }>;
  register: (email: string, password: string, displayName?: string, referralCode?: string, aktionCode?: string) => Promise<{ success: boolean; error?: string; message?: string }>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; error?: string }>;
  refreshTokens: () => Promise<boolean>;
  myPortalFetch: (endpoint: string, options?: RequestInit) => Promise<Response>;
  updateUser: (updates: Partial<UnifiedUser>) => void;
  updateLicenses: (licenses: License[]) => void;
  handleOAuthCallback: (accessToken: string, refreshToken: string) => Promise<{ success: boolean; error?: string }>;
}

const STORAGE_KEY = 'my_portal_auth';
const ETAG_CACHE_KEY = 'my_portal_etag_cache';

// Module-level ETag cache for My Portal API calls
const etagCache = new Map<string, { etag: string; data: unknown; timestamp: number }>();

// Module-level refresh lock: prevents multiple parallel 401 handlers from
// each triggering their own token refresh (race condition → logout).
// 🚨 `refreshTokens()` liefert `boolean | 'server_error'` — mit `Promise<boolean>` verengte
// diese Deklaration das Ergebnis, und die Auswertung `refreshResult === 'server_error'`
// meldete der Compiler als niemals wahr. Zur Laufzeit kam der Wert sehr wohl an; der
// Sonderzweig „Server vorübergehend nicht erreichbar" war also korrekt, nur untypisiert.
let refreshPromise: Promise<boolean | 'server_error'> | null = null;

// Tracks whether the last refresh attempt failed. Prevents a second parallel
// call from creating a NEW refresh promise after the first one's .finally()
// cleared refreshPromise but before logout() has run.
let refreshFailed = false;

// Module-level logout guard: synchronous flag to prevent multiple logout calls.
// React state is async (batched), so a state-based guard fails when two callers
// both read the old state before either update takes effect.
let isLoggingOut = false;

// V-1659: Der Antwort-Cache liegt nur noch im Arbeitsspeicher (frueher als Klartext im
// localStorage, auch nach dem Abmelden lesbar). Alte Bestaende werden einmalig entfernt.
try { localStorage.removeItem(ETAG_CACHE_KEY); } catch {}

/** Verwirft alle gecachten Antworten (Abmelden, Anmelden, Sitzungswechsel). */
function clearETagCache(): void {
  etagCache.clear();
}

// Check for Google OAuth status
export async function checkGoogleOAuthStatus(): Promise<{ enabled: boolean; clientId?: string }> {
  try {
    const apiUrl = localStorage.getItem('api_url') || '/api';
    const response = await fetch(`${apiUrl}/public/auth/google/status`);
    if (response.ok) {
      return await response.json();
    }
  } catch (err) {
    console.error('Failed to check Google OAuth status:', err);
  }
  return { enabled: false };
}

// Basis-Adresse der API (Kundencenter).
export function myPortalApiUrl(): string {
  return localStorage.getItem('api_url') || '/api';
}

// Start Google OAuth flow
// V-2549: Der Start hängt die PKCE-Challenge dieses Tabs an (der Rücksprung trägt nur einen Einmal-Code, nie ein
// Token). Gibt false zurück, wenn der Browser das nicht kann — die Maske zeigt dann eine Fehlermeldung.
export function startGoogleOAuth(tenantId?: string, bedingungenAngenommen?: boolean): Promise<boolean> {
  // tenantId: Multi-Tenant-Login — Auswahl-Zweitlauf nach der Mandanten-Auswahl
  // (läuft bei bestehender Google-Session ohne weitere Interaktion durch).
  // V-2622: bedingungenAngenommen = Unternehmer-Erklärung und AGB wurden in der Maske bestätigt; ohne sie legt der
  // Server für ein unbekanntes Google-Konto kein Konto an (Rücksprung `business_terms_required`).
  return starteGoogleAnmeldung(`${myPortalApiUrl()}/public/auth/google`, {
    tenant_id: tenantId,
    business_terms_accepted: bedingungenAngenommen ? '1' : undefined,
  });
}

// Create the context with a default that throws if used outside provider
const MyPortalAuthContext = createContext<MyPortalAuthContextType | null>(null);

export function MyPortalAuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    licenses: [],
    token: null,
    refreshToken: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const getApiBaseUrl = () => {
    return localStorage.getItem('api_url') || '/api';
  };

  // V-2629 (F-006): Lizenzen zu GENAU diesem Token laden — der einzige Weg, `licenses` nach dem Start zu füllen. Geschrieben
  // wird nur, solange dieses Token gespeichert ist (Übergabe, Anmeldung oder Abmeldung dazwischen = nichts schreiben).
  // Genutzt beim Seitenstart UND nach einer Support-Übergabe (sonst „Lizenz erforderlich“ bis F5).
  const ladeLizenzen = (token: string) => {
    fetch(`${getApiBaseUrl()}/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.licenses) return;
        let gespeichert: any = null;
        try { gespeichert = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { /* unlesbar = nicht schreiben */ }
        if (!darfRefreshSchreiben(token, gespeichert?.token ?? null)) return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...gespeichert, licenses: data.licenses }));
        setAuthState(prev => prev.isAuthenticated && prev.token === token ? { ...prev, licenses: data.licenses } : prev);
      })
      .catch(() => {}); // silent fail — login/refresh will handle auth errors
  };

  // Initialize from localStorage; Support-Zugang per postMessage vom Admin-Fenster (V-2629)
  useEffect(() => {
    // V-2629: Ein Token in der Adresse wird NIE übernommen (Login-CSRF per Link, Token in Verlauf/Referer).
    // Der Parameter wird nur aus der Adresse entfernt und ignoriert.
    const urlParams = new URLSearchParams(window.location.search);
    const mitUebergabe = urlParams.get(UEBERGABE_PARAM) === '1';
    if (urlParams.has('support_token')) {
      urlParams.delete('support_token');
      const rest = urlParams.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
    }

    const ladeGespeicherteSitzung = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed.token && parsed.user) {
            applyAccountLanguage(parsed.user);
            setAuthState({
              user: parsed.user,
              licenses: parsed.licenses || [],
              token: parsed.token,
              refreshToken: parsed.refreshToken || null,
              isLoading: false,
              isAuthenticated: true,
            });
            return;
          }
        } catch (err) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      setAuthState(prev => ({ ...prev, isLoading: false }));
    };

    const opener = window.opener as Window | null;
    if (!mitUebergabe || !opener) {
      if (mitUebergabe) {
        urlParams.delete(UEBERGABE_PARAM);
        const rest = urlParams.toString();
        window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
      }
      ladeGespeicherteSitzung();
      return;
    }

    // Übergabe vom Admin-Fenster: erst „bereit" melden, dann auf das Token warten (nur von window.opener, gleiche Herkunft).
    const origin = window.location.origin;
    let fertig = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const beenden = () => {
      fertig = true;
      window.removeEventListener('message', beiNachricht);
      if (timer) clearTimeout(timer);
      const rest = new URLSearchParams(window.location.search);
      if (rest.has(UEBERGABE_PARAM)) {
        rest.delete(UEBERGABE_PARAM);
        const s = rest.toString();
        window.history.replaceState({}, '', window.location.pathname + (s ? `?${s}` : '') + window.location.hash);
      }
    };
    function beiNachricht(ereignis: MessageEvent) {
      if (fertig || !istGueltigeTokenNachricht(ereignis, opener, origin)) return;
      const supportToken = (ereignis.data as { token: string }).token;
      beenden();
      // Übergabe abgeschlossen: Beziehung zum Admin-Fenster kappen (Reverse Tabnabbing, V-2629 F-001)
      try { window.opener = null; } catch { /* nicht beschreibbar: unkritisch */ }
      const payload = leseSupportClaims(supportToken);
      if (!payload) {
        ladeGespeicherteSitzung();
        return;
      }
      const supportUser = {
        id: payload.tenant_id,
        // 'email'-Claim zuerst: seit 12.08.2026 trägt 'username' die userId (UUID),
        // nicht mehr die E-Mail — Alt-Tokens haben dort weiterhin die E-Mail.
        email: payload.email || payload.username || payload.super_admin_email,
        displayName: `Support: ${payload.tenant_name}`,
        tenantId: payload.tenant_id,
      };
      clearETagCache();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        user: supportUser,
        licenses: [],
        token: supportToken,
        refreshToken: null,
        isSupportSession: true,
        supportAdminName: payload.super_admin_name,
        supportSessionId: payload.session_id,
        supportExpiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null,
      }));
      setAuthState({
        user: supportUser as unknown as UnifiedUser,
        licenses: [],
        token: supportToken,
        refreshToken: null,
        isLoading: false,
        isAuthenticated: true,
      });
      // F-006: Der Start-Refresh lief während der Übergabe bewusst nicht — die Lizenzen jetzt für das Support-Token holen.
      ladeLizenzen(supportToken);
    }
    window.addEventListener('message', beiNachricht);
    timer = setTimeout(() => {
      if (fertig) return;
      beenden();
      ladeGespeicherteSitzung();
    }, UEBERGABE_FRIST_MS);
    try {
      opener.postMessage({ typ: NACHRICHT_BEREIT }, origin);
    } catch {
      beenden();
      ladeGespeicherteSitzung();
    }
    return () => {
      if (!fertig) {
        window.removeEventListener('message', beiNachricht);
        if (timer) clearTimeout(timer);
      }
    };
  }, []);

  // Refresh licenses from API on every page load (background, non-blocking)
  useEffect(() => {
    // V-2629: Während einer Support-Übergabe nicht refreshen (die gespeicherte Sitzung ist die VORHERIGE).
    if (uebergabeLaeuft(window.location.search, window.opener)) return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    let parsed: any;
    try { parsed = JSON.parse(stored); } catch { return; }
    if (!parsed.token) return;

    // V-2629: nur schreiben, wenn die Sitzung inzwischen nicht ausgetauscht wurde (Übergabe, Anmeldung, Abmeldung)
    ladeLizenzen(parsed.token);
  }, []);

  const saveAuth = (data: { user: UnifiedUser; licenses?: License[]; accessToken: string; refreshToken?: string }) => {
    const authData = {
      user: data.user,
      licenses: data.licenses || [],
      token: data.accessToken,
      refreshToken: data.refreshToken || null,
    };
    // Cache nur bei Anmelden/Benutzerwechsel leeren, nicht beim Token-Refresh desselben Benutzers (V-1659)
    let vorherUserId: unknown = null;
    try {
      const vorher = localStorage.getItem(STORAGE_KEY);
      vorherUserId = vorher ? JSON.parse(vorher)?.user?.id ?? null : null;
    } catch { /* unlesbar = wie nicht angemeldet */ }
    if (vorherUserId === null || vorherUserId !== data.user?.id) {
      clearETagCache();
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
    // Account-Sprache beim ersten Login auf diesem Gerät anwenden (No-Op bei lokalem Override)
    applyAccountLanguage(data.user);
    setAuthState({
      ...authData,
      isLoading: false,
      isAuthenticated: true,
    });
    // Reset module-level flags on successful auth
    refreshFailed = false;
    isLoggingOut = false;
  };

  const login = useCallback(async (
    email: string,
    password: string,
    tenantId?: string
  ): Promise<{ success: boolean; error?: string; code?: string; retry_after_seconds?: number; tenants?: Array<{ tenantId: string; companyName: string; userId: string }> }> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/public/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // client: Single-Session-Scope (Kundencenter). Backend setzt nur dann eine durchgesetzte
        // Session + session_id, wenn das Enforcement-Flag aktiv ist (sonst No-Op).
        // tenantId: Multi-Tenant-Login — gewählter Mandant aus dem Auswahl-Schritt.
        body: JSON.stringify({ email, password, client: 'kundencenter', tenantId }),
      });

      const data = await response.json();

      if (!response.ok) {
        // `code` durchreichen — die Anmeldemaske unterscheidet daran „E-Mail noch nicht
        // bestätigt" bzw. „Mandanten-Auswahl nötig" von falschen Zugangsdaten.
        return { success: false, error: data.error || 'Login fehlgeschlagen', code: data.code, retry_after_seconds: data.retry_after_seconds, tenants: data.tenants };
      }

      saveAuth(data);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Verbindungsfehler' };
    }
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    displayName?: string,
    referralCode?: string,
    aktionCode?: string
  ): Promise<{ success: boolean; error?: string; message?: string }> => {
    try {
      const body: Record<string, string | undefined> = { email, password, displayName };
      if (referralCode) {
        body.referralCode = referralCode;
      }
      if (aktionCode) {
        body.aktionCode = aktionCode;
      }
      const response = await fetch(`${getApiBaseUrl()}/public/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Registrierung fehlgeschlagen' };
      }

      // Auto-login after registration
      if (data.accessToken) {
        saveAuth(data);
      }

      return { success: true, message: data.message };
    } catch (err: any) {
      return { success: false, error: err.message || 'Verbindungsfehler' };
    }
  }, []);

  const logout = useCallback(async () => {
    // Synchronous module-level guard: prevents multiple parallel callers from
    // each firing a logout request. React state is async so we can't rely on it.
    if (isLoggingOut) return;
    isLoggingOut = true;

    // Grab token before clearing
    const stored = localStorage.getItem(STORAGE_KEY);
    let currentToken: string | null = null;
    if (stored) {
      try { currentToken = JSON.parse(stored).token; } catch {}
    }

    // Clear state + storage immediately
    clearETagCache();
    localStorage.removeItem(STORAGE_KEY);
    setAuthState({
      user: null,
      licenses: [],
      token: null,
      refreshToken: null,
      isLoading: false,
      isAuthenticated: false,
    });

    try {
      if (currentToken) {
        await fetch(`${getApiBaseUrl()}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
          },
        });
      }
    } catch (err) {
      // Ignore logout errors
    } finally {
      isLoggingOut = false;
    }
  }, []);

  const forgotPassword = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/public/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error };
      }

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }, []);

  const resetPassword = useCallback(async (token: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/public/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error };
      }

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }, []);

  const refreshTokens = useCallback(async (): Promise<boolean | 'server_error'> => {
    if (refreshFailed) return false;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;

    let storedData: { refreshToken?: string; user?: UnifiedUser; licenses?: License[] };
    try { storedData = JSON.parse(stored); } catch { return false; }
    if (!storedData.refreshToken) return false;

    const doRefresh = async (): Promise<Response> => {
      return fetch(`${getApiBaseUrl()}/public/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // client beim Refresh mitsenden, damit das Backend den Single-Session-Status revalidieren
        // kann (verdrängte Session -> Refresh wird verweigert).
        body: JSON.stringify({ refreshToken: storedData.refreshToken, client: 'kundencenter' }),
      });
    };

    try {
      let response = await doRefresh();

      if (response.status >= 500) {
        await new Promise(r => setTimeout(r, 1500));
        response = await doRefresh();
      }

      if (response.status >= 500) {
        return 'server_error';
      }

      if (!response.ok) {
        refreshFailed = true;
        return false;
      }

      refreshFailed = false;
      const data = await response.json();
      saveAuth({
        user: storedData.user!,
        licenses: data.licenses || storedData.licenses || [],
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      return true;
    } catch (err) {
      return 'server_error';
    }
  }, []);

  // Authenticated fetch helper with ETag caching
  const myPortalFetch = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    // Use token from shared context state (guaranteed to be current since Context is shared)
    // Also fall back to localStorage as a safety net
    let token = authState.token;
    let storedRefreshToken = authState.refreshToken;
    if (!token) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          token = parsed.token;
          storedRefreshToken = parsed.refreshToken;
        } catch {}
      }
    }

    if (!token) {
      throw new Error('Not authenticated');
    }

    const method = (options.method || 'GET').toUpperCase();
    const isGetRequest = method === 'GET' || method === 'HEAD';
    const cacheKey = endpoint;

    // Build headers with ETag support
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      ...(options.headers as Record<string, string>),
    };

    // Add If-None-Match for GET requests if we have a cached ETag
    if (isGetRequest) {
      const cached = etagCache.get(cacheKey);
      if (cached) {
        headers['If-None-Match'] = cached.etag;
      }
    }

    // Die zuletzt tatsaechlich gesendeten Header — nach einem Token-Refresh tragen sie das
    // neue Bearer-Token. Der 304-Rueckfall weiter unten muss auf DIESEN Stand aufsetzen,
    // sonst wiederholt er den Request mit dem bereits abgelaufenen Token.
    let aktiveHeaders: Record<string, string> = headers;

    let response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      const freshStored = localStorage.getItem(STORAGE_KEY);
      const freshRefreshToken = freshStored ? JSON.parse(freshStored).refreshToken : null;

      if (freshRefreshToken) {
        if (!refreshPromise) {
          refreshPromise = refreshTokens().finally(() => { refreshPromise = null; });
        }
        const refreshResult = await refreshPromise;

        if (refreshResult === 'server_error') {
          throw new Error('Server vorübergehend nicht erreichbar');
        }

        if (refreshResult === true) {
          const newStored = localStorage.getItem(STORAGE_KEY);
          if (newStored) {
            const parsed = JSON.parse(newStored);
            const retryHeaders = { ...headers, 'Authorization': `Bearer ${parsed.token}` };
            aktiveHeaders = retryHeaders;
            response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
              ...options,
              headers: retryHeaders,
            });
          }
        }
      }
    }

    if (response.status === 401) {
      // Grund (z. B. 'session_superseded' = auf anderem Gerät angemeldet) für die
      // Login-Seite hinterlegen, damit dort eine differenzierte Meldung erscheint.
      try {
        const reason = (await response.clone().json())?.error;
        if (reason === 'session_superseded') {
          sessionStorage.setItem('my_portal_session_reason', reason);
        }
      } catch {
        // Body nicht lesbar -> generische Meldung
      }
      logout();
      throw new Error('Session abgelaufen');
    }

    // Handle 304 Not Modified - return cached response
    if (response.status === 304 && isGetRequest) {
      const cached = etagCache.get(cacheKey);
      if (cached) {
        // Create a synthetic Response with cached data
        return new Response(JSON.stringify(cached.data), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      // 🚨 304 OHNE passenden Cache-Eintrag: Der Rumpf einer 304-Antwort ist per HTTP leer.
      // Frueher fiel der Code hier einfach durch und gab die leere Antwort zurueck — jeder
      // Aufrufer lief damit in `res.json()` auf einem leeren Rumpf, bekam eine Ausnahme und
      // zeigte seine allgemeine Fehlermeldung ("Fehler beim Laden ..."), obwohl der Server
      // voellig in Ordnung geantwortet hatte. Der Fall tritt real auf: der Eintrag faellt aus
      // dem Speicher-Cache (30-Minuten-Grenze beim Laden aus dem localStorage, geleerter
      // Speicher, privates Fenster), waehrend der Browser den ETag noch kennt.
      // Deshalb: einmal ohne `If-None-Match` erneut anfragen und die volle Antwort holen.
      const ohneEtag = { ...aktiveHeaders };
      delete ohneEtag['If-None-Match'];
      etagCache.delete(cacheKey);
      response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
        ...options,
        headers: ohneEtag,
        cache: 'reload',
      });
    }

    // Store ETag from successful GET responses
    if (isGetRequest && response.ok) {
      const etag = response.headers.get('ETag');
      if (etag) {
        // Clone response so we can read the body for caching
        const cloned = response.clone();
        try {
          const data = await cloned.json();
          etagCache.set(cacheKey, { etag, data, timestamp: Date.now() });
        } catch {}
      }
    }

    // Invalidate ETag cache for related GET endpoints after successful mutations
    if (!isGetRequest && response.ok) {
      const basePath = endpoint.replace(/\/[^/]+$/, '');
      for (const key of etagCache.keys()) {
        if (key === endpoint || key === basePath) {
          etagCache.delete(key);
        }
      }
    }

    return response;
  }, [authState.token, authState.refreshToken, refreshTokens, logout]);

  const updateUser = useCallback((updates: Partial<UnifiedUser>) => {
    if (!authState.user) return;
    const newUser = { ...authState.user, ...updates };
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      parsed.user = newUser;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    }
    setAuthState(prev => ({ ...prev, user: newUser }));
  }, [authState.user]);

  const updateLicenses = useCallback((licenses: License[]) => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      parsed.licenses = licenses;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    }
    setAuthState(prev => ({ ...prev, licenses }));
  }, []);

  // Handle OAuth callback - fetch user data with the received tokens
  const handleOAuthCallback = useCallback(async (
    accessToken: string,
    refreshToken: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Use the token to get user data
      const response = await fetch(`${getApiBaseUrl()}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        return { success: false, error: 'Fehler beim Abrufen der Benutzerdaten' };
      }

      const data = await response.json();

      saveAuth({
        user: data.user,
        licenses: data.licenses || [],
        accessToken: accessToken,
        refreshToken: refreshToken,
      });

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Verbindungsfehler' };
    }
  }, []);

  const value: MyPortalAuthContextType = {
    ...authState,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshTokens,
    myPortalFetch,
    updateUser,
    updateLicenses,
    handleOAuthCallback,
  };

  return (
    <MyPortalAuthContext.Provider value={value}>
      {children}
    </MyPortalAuthContext.Provider>
  );
}

export function useMyPortalAuth(): MyPortalAuthContextType {
  const context = useContext(MyPortalAuthContext);
  if (!context) {
    throw new Error('useMyPortalAuth must be used within a MyPortalAuthProvider');
  }
  return context;
}

/**
 * Non-throwing Variante: liefert den MyPortal-Auth-Context ODER null, wenn außerhalb
 * eines MyPortalAuthProvider gerendert (z.B. wenn eine /my-Seite in der BIS-ERP-Hauptapp
 * eingehängt wird). Erlaubt geteilten Seiten, im Kundencenter den reicheren myPortalFetch
 * (ETag + 401-Refresh) zu nutzen und in der ERP-App auf dmsFetch zurückzufallen.
 */
export function useMyPortalAuthOptional(): MyPortalAuthContextType | null {
  return useContext(MyPortalAuthContext);
}
